import { useEffect, useMemo } from 'react';
import { Card, CardContent } from '@ui/card.tsx';
import { TrendingUp, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import type { ColumnDef } from '@tanstack/react-table';
import type {
  LogicTypeStepHistoryCompanyCollectionReadModel,
  LogicTypeStepWorkspaceReadAdminWorkspaceReadModel,
  LogicTypeStepCompanyCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import pointsTranslation from '@r/translations/points-translation.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';

type WorkspaceStep = LogicTypeStepWorkspaceReadAdminWorkspaceReadModel;

// Entrée d'historique enrichie avec description et stepPoint depuis workspaceSteps
type EnrichedStepHistory = LogicTypeStepHistoryCompanyCollectionReadModel & {
  description?: string | null;
  stepPoint?: number;
};

interface CompanyStepsBlocProps {
  // Dernier palier officiellement validé par le backend (peut différer du palier calculé côté front si les points ont expiré entre-temps)
  lastStepReached?: LogicTypeStepCompanyCollectionReadModel | null;
  // Solde de points actif (balance), pas acquiredPoints qui est le cumul historique total
  acquiredPoints: number;
  // Historique des paliers de la logique courante uniquement (issu de company.logicStepHistory)
  logicStepHistory?: LogicTypeStepHistoryCompanyCollectionReadModel[];
  workspaceSteps?: WorkspaceStep[];
}

const EMPTY_LOGIC_STEP_HISTORY: LogicTypeStepHistoryCompanyCollectionReadModel[] = [];
const EMPTY_WORKSPACE_STEPS: WorkspaceStep[] = [];

const useCompanyStepHistoryColumns = (): ColumnDef<EnrichedStepHistory>[] => {
  const { t } = useTranslation();
  return [
    {
      id: 'stepName',
      accessorKey: 'logicTypeStep.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.step.name')} />
      ),
      cell: ({ row }) => row.original.logicTypeStep?.name ?? ''
    },
    {
      id: 'stepDescription',
      header: t('label.step.description'),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="block max-w-[200px] truncate"
          title={row.original.description ?? undefined}
        >
          {row.original.description}
        </span>
      )
    },
    {
      id: 'stepPoint',
      header: t('label.step.point'),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.stepPoint != null ? (
          <FormattedNumber value={row.original.stepPoint} style="decimal" />
        ) : (
          ''
        )
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.step.reached.at')} />
      ),
      cell: ({ row }) =>
        row.original.createdAt
          ? format(new Date(row.original.createdAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    }
  ];
};

export function CompanyStepsBloc({
  lastStepReached,
  acquiredPoints,
  logicStepHistory = EMPTY_LOGIC_STEP_HISTORY,
  workspaceSteps = EMPTY_WORKSPACE_STEPS
}: CompanyStepsBlocProps) {
  const { t } = useTranslation();

  const sortedSteps = useMemo(
    () => [...workspaceSteps].sort((a, b) => a.stepPoint - b.stepPoint),
    [workspaceSteps]
  );

  // Palier courant déduit du solde actif (balance), utilisé pour positionner l'indicateur sur la barre.
  // Distinct de lastStepReached qui vient du backend et peut être décalé après une expiration de points.
  const currentReachedStep = sortedSteps.filter((s) => acquiredPoints >= s.stepPoint).pop();

  // Calcul de la progression par segment entre paliers.
  // Chaque segment contribue à hauteur de son avancement, puis on moyenne l'ensemble
  // pour obtenir un pourcentage de remplissage linéaire sur toute la barre.
  const stepAccomplishments = sortedSteps.map((step, idx) => {
    const prevPoint = idx === 0 ? 0 : (sortedSteps[idx - 1].stepPoint ?? 0);
    const range = step.stepPoint - prevPoint;
    if (acquiredPoints >= step.stepPoint) return 100;
    if (acquiredPoints <= prevPoint) return 0;
    return Math.round(((acquiredPoints - prevPoint) / range) * 100);
  });

  const progressValue =
    stepAccomplishments.length > 0
      ? stepAccomplishments.reduce((sum, a) => sum + a, 0) / stepAccomplishments.length
      : 0;

  const enrichedHistory = useMemo<EnrichedStepHistory[]>(() => {
    const workspaceStepMap = new Map(sortedSteps.map((s) => [s.name, s]));
    return logicStepHistory.map((entry) => {
      const ws = workspaceStepMap.get(entry.logicTypeStep.name);
      return { ...entry, description: ws?.description, stepPoint: ws?.stepPoint };
    });
  }, [logicStepHistory, sortedSteps]);

  const columns = useCompanyStepHistoryColumns();
  const { table } = useDataTable({
    data: enrichedHistory,
    columns,
    enableAdvancedFilter: false,
    manualPaginate: false,
    manualSort: false,
    manualFilter: false
  });

  // Tri par défaut : passage le plus récent en premier
  const sorting = table.getState().sorting;
  useEffect(() => {
    if (sorting.length > 0) return;
    table.setSorting([{ id: 'createdAt', desc: true }]);
  }, [sorting.length, table]);

  return (
    <Card className="w-full">
      <CardContent className="flex flex-col p-10 space-y-5 w-full">
        <div className="flex items-center space-x-2.5">
          <TrendingUp />
          <h2 className="text-xl font-bold">{t('admin.company.steps.title')}</h2>
        </div>

        <div className="grid grid-cols-4 gap-6">
          {/* Palier actuel issu du backend */}
          <Card className="col-span-1">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2.5 mb-2">
                <Award className="w-5 h-5" />
                <h3 className="font-semibold">{t('label.company.current.step')}</h3>
              </div>
              <div className="text-4xl font-bold">
                {lastStepReached?.name ?? (
                  <span className="text-muted-foreground text-2xl">
                    {t('admin.company.step.none')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Barre de progression — affichée uniquement si des paliers sont définis sur le programme */}
          {sortedSteps.length > 0 && (
            <Card className="col-span-3">
              <CardContent className="pt-6">
                <div className="px-5 pt-[3.6rem] pb-[2.5rem] sm:px-16">
                  <div className="relative">
                    {/* Indicateur flottant positionné selon le solde actif */}
                    <div
                      className="relative flex-1 w-full h-full transition-all"
                      style={{ transform: `translateX(-${100 - progressValue}%)` }}
                    >
                      <div className="absolute top-[-4rem] right-0 flex translate-x-[50%] flex-nowrap items-center whitespace-nowrap rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground xl:flex">
                        <Award size={13} />
                        <span className="hidden sm:ml-2 sm:block">
                          {currentReachedStep?.name ?? t('admin.company.step.none')}
                        </span>
                        <div className="absolute bottom-[-1.2rem] left-0 hidden w-full justify-center xl:flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            className="stroke-primary fill-primary"
                          >
                            <path d="M10 16L2 4h16L10 16z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Dots : un point par palier + un point à 0 */}
                    {Array.from({ length: sortedSteps.length + 1 }).map((_, index) => (
                      <div
                        key={index}
                        className="absolute top-0 z-40 box-border flex aspect-square h-full -translate-x-1/2 items-center justify-center rounded-full bg-foreground"
                        style={{ left: `${(index / sortedSteps.length) * 100}%` }}
                      >
                        <div className="w-1 h-1 rounded-full bg-background" />
                        <div className="absolute bottom-[-2.3rem] text-foreground md:bottom-[-3.5rem] lg:bottom-[-2.3rem] xl:bottom-[-3.5rem] 2xl:bottom-[-2.3rem]">
                          <span className="hidden whitespace-nowrap md:inline-block text-xs">
                            {index === 0 ? (
                              `0 ${pointsTranslation(t, 0, false)}`
                            ) : (
                              <>
                                <FormattedNumber
                                  value={sortedSteps[index - 1].stepPoint}
                                  style="decimal"
                                />{' '}
                                {pointsTranslation(t, sortedSteps[index - 1].stepPoint, false)}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}

                    <div className="overflow-hidden relative w-full h-3 rounded-full bg-secondary">
                      <div
                        className="relative flex-1 w-full h-full rounded-full transition-all bg-primary"
                        style={{ transform: `translateX(-${100 - progressValue}%)` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DataTable table={table} />
      </CardContent>
    </Card>
  );
}
